/**
 * SettingsCard
 *
 * Panel wrapper for grouped settings forms. The visual primitive that
 * unifies the workspace settings page (General / Access / Dashboard)
 * with the user profile page so the admin shell reads with one
 * language regardless of which surface the admin is on.
 *
 * Anatomy:
 *   • 4px primary-color accent rail on the left (matches the
 *     SectionHeader `accent` variant for visual continuity).
 *   • Title + optional subtitle + optional action slot in a compact
 *     header strip, separated from content by a divider.
 *   • White body with 20px padding; consumers stack rows or use a
 *     `Grid` for form fields. For a label-left/control-right pattern,
 *     pair with `SettingsRow`.
 *
 * Adapted from the WorkspaceEdit private helper; extracted here so
 * other settings-shaped pages can reuse the same primitive without
 * cross-importing from a page file.
 */

import React from 'react';
import { Box, Typography } from '../ui';

export interface SettingsCardProps {
  title: string;
  subtitle?: string;
  /** Optional right-aligned action component (Manage button, etc.) */
  action?: React.ReactNode;
  /** Body content — usually rows or a Grid of form fields. */
  children: React.ReactNode;
  /** Override the body padding/styles. Defaults to `p: 2.5`. */
  bodySx?: any;
  /** Override the card-level styles (outer wrapper). */
  sx?: any;
}

export default function SettingsCard({
  title,
  subtitle,
  action,
  children,
  bodySx,
  sx,
}: SettingsCardProps) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          px: 2.5,
          py: 1.5,
          borderLeft: '4px solid',
          borderLeftColor: 'primary.main',
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Box>
      <Box sx={{ p: 2.5, ...bodySx }}>{children}</Box>
    </Box>
  );
}
